import { defineStore } from 'pinia'
import {api, authApi} from '@/app/services/api.service'
import type { LoginState } from './types/login.types'
import { persistRefreshTokenExpiryTime, touchSession } from '@/composables/tokenRefresh'

export const useLoginStore = defineStore('login', {
  state: (): LoginState => ({
    loginData: null,
    logoutData: null,
    userProfileData: null,
    requestPassword: null,
    newPassword: null,
    setToken: null,
    userLogout: null,
    loginError: null,
    otpData: null,
  }),

  getters: {
    getLoginData: (state) => state.loginData,
    getUserLogoutSuccess: (state) => state.logoutData,
    getUserProfileData: (state) => state.userProfileData,
    getRequestPassword: (state) => state.requestPassword,
    getNewPassword: (state) => state.newPassword,
    getSetToken: (state) => state.setToken,
    getUserLogout: (state) => state.userLogout,
    getLoginError: (state) => state.loginError,
    getOtpSuccess: (state) => state.otpData,
  },

  actions: {
    async doLoginUser(data: any) {
      try {
        delete api().defaults.headers.common['Authorization']
        const response = await authApi().post('auth/api/auth/signin/backoffice', data)
        this.loginData = response.data
      } catch (error: any) {
        sessionStorage.removeItem('token')
        this.loginError = error.response?.data
      }
    },

    async getUserProfile(token: string) {
      try {
        const response = await api().get('profile', {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        })
        const user = response.data
        sessionStorage.setItem('userProfile', JSON.stringify(user))
        this.userProfileData = user
        persistRefreshTokenExpiryTime(user.refreshTokenExpiryTime)
        touchSession()

        if (user.type === 'Client') {
          const abilities = user.active_permissions || []
          sessionStorage.setItem('abilities', JSON.stringify(abilities))
        }
      } catch (error: any) {
        sessionStorage.removeItem('userProfile')
        touchSession()
        this.loginError = error.response?.data
      }
    },

    async requestResetCode(data: any) {
      try {
        const response = await api().post('request-reset', data)
        this.requestPassword = response.data
      } catch (error: any) {
        this.loginError = error.response?.data
      }
    },

    async changePassword(data: any) {
      try {
        const response = await api().post('set-password', data)
        this.newPassword = response.data
      } catch (error: any) {
        sessionStorage.removeItem('token')
        this.loginError = error.response?.data
      }
    },

    async checkTokenVerification(data: string) {
      try {
        const response = await api().get('validate-token?token=' + data)
        this.setToken = response.data
      } catch (error: any) {
        sessionStorage.removeItem('token')
        this.loginError = error.response?.data
      }
    },

    async doLogoutUser(data: any) {
      try {
        const response = await api().post('auth/api/auth/logout/at-login', data)
        this.logoutData = response.data
      } catch (error: any) {
        this.loginError = error.response?.data
      }
    },

    async doLogoutAccount() {
      try {
        const response = await api().get('/auth/api/auth/logout')
        this.userLogout = response.data
      } catch (error: any) {
        this.loginError = error.response?.data
      }
    },

    async doInvalidateSession(data: any) {
      try {
        const response = await api().post('/auth/api/auth/logout/at-login', data)
        this.userLogout = response.data
      } catch (error: any) {
        this.loginError = error.response?.data
      }
    },

    async doOtpVerification(data: any) {
      try {
        const response = await authApi().post('auth/api/auth/validate-login-code', data)
        this.otpData = response.data
      } catch (error: any) {
        this.loginError = error.response?.data
      }
    },

    setCurrentUserProfile(user: any) {
      this.userProfileData = user
      touchSession()
    },
  },
})
